import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useField } from "@unform/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "../../../shared/hooks";
import { clientService } from "../../../shared/services/api/client/clientService";

type TAutoCompleteOption = { id: string; label: string };

interface IAutoCompleteClientFireProps {
	isExternalLoading?: boolean;
	labelSelect?: string;
	namecampo?: string;
	disabled?: boolean;
	onChange?: (id: string | null) => void;
	nameDefault?: string;
	minChars?: number;
}

export const AutoCompleteClientFire: React.FC<IAutoCompleteClientFireProps> = ({
	isExternalLoading = false,
	labelSelect = "",
	namecampo = "clientId",
	onChange,
	disabled = false,
	nameDefault = "",
	minChars = 2,
}) => {
	// Unform
	const { fieldName, registerField, defaultValue, error, clearError } = useField(namecampo);
	const { debounce } = useDebounce();

	// Estados
	const [selectedId, _setSelectedId] = useState<string | undefined>((defaultValue as string | undefined) ?? undefined);
	const selectedIdRef = useRef<string | undefined>(selectedId);
	const setSelectedId = (id?: string) => {
		selectedIdRef.current = id;
		_setSelectedId(id);
	};

	const [busca, setBusca] = useState("");
	const [debouncedBusca, setDebouncedBusca] = useState("");
	const [options, setOptions] = useState<TAutoCompleteOption[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const resolvedInitialRef = useRef(false);
	const suppressNextSearchRef = useRef(false);
	const reqSeq = useRef(0);
	const lastSelectedRef = useRef<TAutoCompleteOption | null>(null);

	// Registro Unform
	useEffect(() => {
		registerField({
			name: fieldName,
			getValue: () => selectedIdRef.current,
			setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
			clearValue: () => setSelectedId(undefined),
		});
	}, [registerField, fieldName]);

	// Debounce da busca digitada
	useEffect(() => {
		const cancel = debounce(() => setDebouncedBusca((busca || "").trim()));
		return () => void cancel;
	}, [busca]);

	// Resolver valor inicial
	useEffect(() => {
		if (!selectedIdRef.current || !nameDefault || resolvedInitialRef.current) return;
		resolvedInitialRef.current = true;

		setIsLoading(true);
		const myReq = ++reqSeq.current;

		(async () => {
			try {
				const result = await clientService.getAll(1, nameDefault, "", "", "active");
				if (!(result instanceof Error)) {
					const mapped = result.data.map((c: any) => ({ id: c.id, label: c.name })) as TAutoCompleteOption[];
					const hasSelected = mapped.some((o) => o.id === selectedIdRef.current);

					if (myReq === reqSeq.current) {
						setOptions(hasSelected ? mapped : [...mapped, { id: selectedIdRef.current as string, label: nameDefault }]);
					}
				}
			} finally {
				if (myReq === reqSeq.current) setIsLoading(false);
			}
		})();
	}, [nameDefault]);

	// Busca dinâmica digitada
	useEffect(() => {
		const term = debouncedBusca;

		if (suppressNextSearchRef.current) {
			suppressNextSearchRef.current = false;
			return;
		}

		if (term.length < minChars) {
			// Se o termo de busca é muito curto, apenas limpa as opções
			// O valor selecionado será mantido via valueOption e displayedOptions
			if (!selectedIdRef.current) {
				setOptions([]);
			}
			return;
		}

		setIsLoading(true);
		const myReq = ++reqSeq.current;

		(async () => {
			try {
				const result = await clientService.getAll(1, term, "", "", "active");
				if (!(result instanceof Error)) {
					const mapped = result.data.map((c: any) => ({ id: c.id, label: c.name })) as TAutoCompleteOption[];
					if (myReq === reqSeq.current) setOptions(mapped);
				}
			} finally {
				if (myReq === reqSeq.current) setIsLoading(false);
			}
		})();
	}, [debouncedBusca, minChars]);

	// 🔥 CORREÇÃO DO VALOR SELECIONADO (principal bug)
	const valueOption = useMemo(() => {
		const id = selectedIdRef.current;
		if (!id) return null;

		// Prioriza última seleção REAL (evita campo em branco)
		if (lastSelectedRef.current?.id === id) {
			return lastSelectedRef.current;
		}

		// Se a opção existe na lista, usa ela
		const found = options.find((o) => o.id === id);
		if (found) return found;

		// Se temos label inicial, usa
		if (nameDefault) return { id, label: nameDefault };

		// Fallback melhorado
		return { id, label: "(carregando...)" };
	}, [options, nameDefault]);

	// Garante que o valor sempre aparece na lista
	const displayedOptions = useMemo(() => {
		const val = valueOption;
		if (val && !options.some((o) => o.id === val.id)) {
			return [val, ...options];
		}
		return options;
	}, [options, valueOption]);

	return (
		<Autocomplete<TAutoCompleteOption, false, false, false>
			openText="Abrir"
			closeText="Fechar"
			noOptionsText="Nenhuma opção"
			loadingText="Carregando…"
			disablePortal
			value={valueOption}
			options={displayedOptions}
			getOptionLabel={(o) => o?.label ?? ""}
			isOptionEqualToValue={(opt, val) => opt.id === val.id}
			loading={isLoading}
			fullWidth
			disabled={isExternalLoading || disabled}
			onInputChange={(_, newInput, reason) => {
				// 🚫 IGNORAR qualquer mudança causada por blur
				if (reason === "blur") return;

				// 🚫 IGNORAR resets internos do MUI
				if (reason === "reset") return;

				// 🚫 IGNORAR quando o usuário limpou via clear
				if (reason === "clear") {
					setBusca("");
					return;
				}

				// 🚫 BLOQUEAR chamadas artificiais
				if (suppressNextSearchRef.current) {
					suppressNextSearchRef.current = false;
					return;
				}

				// ✔️ Só atualizar busca se realmente digitou
				if (reason === "input") {
					if (error) clearError?.();
					setBusca((newInput || "").trimStart());
				}
			}}
			onChange={(_, newValue) => {
				const id = newValue?.id;
				const newSelectedId = id || undefined;

				if (newSelectedId === selectedIdRef.current) {
					suppressNextSearchRef.current = true;
					return;
				}

				suppressNextSearchRef.current = true;

				// 🔥 Salva o item REAL selecionado
				lastSelectedRef.current = newValue ?? null;

				// 🚀 AJUSTE CRUCIAL: Limpar a busca e as opções ao selecionar para estabilizar o campo
				setBusca("");
				setOptions(newValue ? [newValue] : []);

				setSelectedId(newSelectedId);
				clearError?.();

				if (onChange) onChange(id || null);
			}}
			popupIcon={isExternalLoading || isLoading ? <CircularProgress size={28} /> : undefined}
			renderInput={(params) => <TextField {...params} label={labelSelect} error={!!error} helperText={error} disabled={disabled} fullWidth />}
		/>
	);
};
